<template>
  <v-dialog
    :value="value"
    persistent
    max-width="820px"
    @click:outside="closeModal">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="warning">mdi-alert</v-icon>
        <span class="pl-2">Rule Deviations<span v-if="storeName"> — {{ storeName }}</span></span>
        <v-spacer/>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="pt-2 pb-1">
        These rules aren't on every store in the group, so they're hidden from the group view.
        Use a row's actions to sync the rule to the stores missing it, or disable it on every store that has it.
      </v-card-subtitle>

      <v-data-table
        :headers="headers"
        :items="deviations"
        :items-per-page="-1"
        dense
        hide-default-footer
        :loading="loading"
        loading-text="Updating store rules…"
        no-data-text="No rule deviations — every store shares the same rules.">

        <template #item.rule="{ item }">
          <td class="py-2">
            <div class="font-weight-medium">
              {{ item.rep.rule_display_name }}
              <v-chip v-if="item.rep.enabled === false" x-small color="grey" text-color="white" class="ml-1">Disabled</v-chip>
            </div>
            <div v-if="deviationCaption(item.rep)" class="text-caption grey--text text--darken-1">
              {{ deviationCaption(item.rep) }}
            </div>
          </td>
        </template>

        <template #item.present="{ item }">
          <td class="py-2">
            <v-chip x-small color="success" text-color="white" class="mr-2 font-weight-bold">
              {{ item.present.length }}
            </v-chip>
            <span class="text-caption">{{ item.present.join(', ') }}</span>
          </td>
        </template>

        <template #item.missing="{ item }">
          <td class="py-2">
            <v-chip x-small color="error" text-color="white" class="mr-2 font-weight-bold">
              {{ item.missing.length }}
            </v-chip>
            <span class="text-caption">{{ item.missing.join(', ') }}</span>
          </td>
        </template>

        <template #item.actions="{ item }">
          <td class="text-end">
            <v-menu offset-y left>
              <template #activator="{ on }">
                <v-btn icon small :disabled="loading" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="$emit('apply-to-all', item)">
                  <v-list-item-icon><v-icon color="success">mdi-store-plus</v-icon></v-list-item-icon>
                  <v-list-item-title>Add to {{ item.missing.length }} missing store{{ item.missing.length === 1 ? '' : 's' }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="item.rep.enabled !== false" @click="disableTarget = item">
                  <v-list-item-icon><v-icon>mdi-cancel</v-icon></v-list-item-icon>
                  <v-list-item-title>Disable on all stores</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </template>
      </v-data-table>

      <v-dialog :value="!!disableTarget" persistent max-width="460" @click:outside="disableTarget = null">
        <v-card v-if="disableTarget">
          <v-card-title class="main white--text pa-2">
            <v-icon color="white" class="mr-2">mdi-cancel</v-icon>
            Disable rule on the group?
          </v-card-title>
          <v-card-text class="pa-4">
            This disables <strong>{{ disableTarget.rep.rule_display_name }}</strong> on the
            {{ disableTarget.present.length }}
            store{{ disableTarget.present.length === 1 ? '' : 's' }} that currently have it:
            <strong>{{ disableTarget.present.join(', ') }}</strong>.
            You can re-enable it from Show disabled rules.
          </v-card-text>
          <v-card-actions class="pa-3 pt-0">
            <v-spacer/>
            <v-btn text @click="disableTarget = null">Cancel</v-btn>
            <v-btn color="primary" @click="confirmDisable">Disable</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>
<script>
export default {
  name: 'RuleDeviationsModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    storeName: {
      type: String,
      default: ''
    },
    deviations: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      disableTarget: null,
      headers: [
        { text: 'Rule', value: 'rule', class: 'accent white--text', align: 'start', sortable: false },
        { text: 'On Stores', value: 'present', class: 'accent white--text', sortable: false },
        { text: 'Missing From', value: 'missing', class: 'accent white--text', sortable: false },
        { text: '', value: 'actions', class: 'accent white--text', sortable: false, align: 'end', width: 56 }
      ]
    }
  },
  methods: {
    closeModal() {
      this.$emit('update:value', false)
    },
    confirmDisable() {
      this.$emit('disable-on-all', this.disableTarget)
      this.disableTarget = null
    },
    deviationValue(rule) {
      if (rule.label === 'Bracket') return this.formatBrackets(rule.brackets)
      if (rule.label && rule.label !== 'None') return this.formatRuleValue(rule.rule_value, rule.label)
      return ''
    },
    deviationCaption(rule) {
      const filter = rule.filter_lvl ? `${this.getRuleDisplay(rule.filter_lvl)}: ${rule.filter_value}` : ''
      return [this.deviationValue(rule), filter].filter(Boolean).join(' · ')
    },
    formatRuleValue(value, type) {
      if (type === 'Percentage') return `${value}%`
      if (type === 'Number') return `$${value}`
      return value
    },
    formatBrackets(brackets) {
      if (!brackets || !brackets.length) return ''
      return brackets
        .map(bracket => `$${bracket.min}-$${bracket.max} → ${bracket.units}/$${bracket.price}`)
        .join(', ')
    },
    getRuleDisplay(key) {
      const ruleMapping = {
        ad_group: 'Ad Group',
        store: 'Store',
        department: 'Department',
        category: 'Category',
        subcategory: 'Subcategory'
      }
      return ruleMapping[key] || key
    }
  }
}
</script>
